import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Section from '../components/Section';

const slides = [
  {
    title: 'Steganography',
    subtitle: 'Hidden in Plain Sight',
    tags: ['steghide', 'zsteg', 'binwalk', 'exiftool', 'APT'],
    icon: '👁️',
    accent: '#a78bfa',
    href: '/slides/steganography/index.html',
    badge: 'New',
  },
  {
    title: 'Cryptography',
    subtitle: 'From Theory to Real-World Attacks',
    tags: ['AES', 'RSA', 'hashing', 'attacks', 'CTF'],
    icon: '🔐',
    accent: '#00ff88',
    href: '/slides/cryptography/index.html',
    badge: null,
  },
  {
    title: 'CTFs',
    subtitle: 'Hack Your Career',
    tags: ['web', 'pwn', 'rev', 'forensics', 'career'],
    icon: '🚩',
    accent: '#f59e0b',
    href: '/slides/ctf/reveal.html',
    badge: null,
  },
  {
    title: 'Networking',
    subtitle: 'Practical Guide',
    tags: ['TCP/IP', 'subnetting', 'DNS', 'Wireshark'],
    icon: '🌐',
    accent: '#38bdf8',
    href: '/slides/networking.html',
    badge: null,
  },
  {
    title: 'OSI Model',
    subtitle: 'Layer by Layer',
    tags: ['layers', 'protocols', 'attacks', 'networking'],
    icon: '📡',
    accent: '#34d399',
    href: '/slides/osi.html',
    badge: null,
  },
  {
    title: 'Linux',
    subtitle: 'Introduction',
    tags: ['bash', 'permissions', 'filesystem', 'scripting'],
    icon: '🐧',
    accent: '#fb923c',
    href: '/slides/linux.html',
    badge: null,
  },
  {
    title: 'Prompt Engineering',
    subtitle: 'Getting the Most from LLMs',
    tags: ['LLM', 'GPT', 'RAG', 'chain-of-thought', 'jailbreak'],
    icon: '🤖',
    accent: '#e879f9',
    href: null,
    badge: 'Coming Soon',
  },
  {
    title: 'AI Security',
    subtitle: 'Attacking & Defending LLMs',
    tags: ['prompt injection', 'adversarial', 'OWASP LLM', 'red team'],
    icon: '🧠',
    accent: '#f43f5e',
    href: null,
    badge: 'Coming Soon',
  },
];

function SlidePreview({ href, accent }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.21);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / 1280);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  if (!href) {
    return (
      <div className="slide-preview-wrap slide-preview-soon" style={{ '--accent': accent }}>
        <span className="slide-preview-soon-label">Coming Soon</span>
      </div>
    );
  }

  return (
    <div className="slide-preview-wrap" ref={wrapRef}>
      <div
        className="slide-preview-inner"
        style={{ transform: `scale(${scale})` }}
      >
        <iframe
          src={href}
          title="slide preview"
          scrolling="no"
          tabIndex={-1}
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
}

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.08, ease: 'easeOut' },
  }),
};

export default function Slides() {
  return (
    <Section id="slides" title="Slides">
      <div className="slides-grid">
        {slides.map((s, i) => (
          <motion.a
            key={s.title}
            href={s.href || undefined}
            target={s.href ? '_blank' : undefined}
            rel={s.href ? 'noopener noreferrer' : undefined}
            className={`slide-card${!s.href ? ' slide-card--soon' : ''}`}
            style={{ '--accent': s.accent }}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={cardVariants}
            whileHover={s.href ? { y: -4, transition: { duration: 0.2 } } : {}}
          >
            <SlidePreview href={s.href} accent={s.accent} />

            <div className="slide-card-meta">
              <div className="slide-card-top">
                <div className="slide-card-heading">
                  <span className="slide-icon">{s.icon}</span>
                  <h3 className="slide-title">{s.title}</h3>
                </div>
                {s.badge && <span className="slide-badge">{s.badge}</span>}
              </div>
              <p className="slide-subtitle">{s.subtitle}</p>
              <div className="slide-tags">
                {s.tags.map((t) => (
                  <span key={t} className="slide-tag">{t}</span>
                ))}
              </div>
              <div className="slide-footer">
                <span className="slide-open">{s.href ? 'Open slides →' : 'In progress...'}</span>
              </div>
            </div>
          </motion.a>
        ))}
      </div>
    </Section>
  );
}
